import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { updateUrlFilters } from '../../../utils/urlFilters'
import { showToast } from '../../shared/Toast'
import AlertsService from '../../../services/api/AlertsService'
import { TOAST_CONTAINER_ID } from '../TriggeredAlerts/constants'

const SAVE_ERROR_MSG = 'Préférences non sauvegardées. Veuillez réessayer plus tard.'

const COLUMNS_PARAM = 'columns'
const VARIABLES_PARAM = 'variables'

const readUrlIdsParam = param => {
  const raw = new URLSearchParams(window.location.search).get(param)
  if (!raw)
    return null
  return raw.split(',').map(Number).filter(Number.isInteger)
}

const syncUrl = (questionIds, variableIds) => {
  updateUrlFilters({
    [COLUMNS_PARAM]: questionIds.length === 0 ? null : questionIds.join(','),
    [VARIABLES_PARAM]: variableIds.length === 0 ? null : variableIds.join(',')
  })
}

const initialPreferences = (initial, defaults) => {
  const base = AlertsService.transformPreferences(initial, defaults)
  // URL params override the persisted state — useful for shared links.
  const urlQuestionIds = readUrlIdsParam(COLUMNS_PARAM)
  const urlVariableIds = readUrlIdsParam(VARIABLES_PARAM)
  return {
    ...base,
    questionIds: urlQuestionIds || base.questionIds,
    variableIds: urlVariableIds || base.variableIds
  }
}

const useColumnPreferences = (collectId, defaults, initial = null) => {
  const [preferences, setPreferences] = useState(() => initialPreferences(initial, defaults))
  // Reset state when collectId changes — we don't carry preferences across collects.
  const previousCollectId = useRef(collectId)
  useEffect(() => {
    if (previousCollectId.current === collectId)
      return
    previousCollectId.current = collectId
    setPreferences(initialPreferences(initial, defaults))
  }, [collectId, defaults, initial])

  // Fire-and-forget save: the local state is updated optimistically. On
  // Failure we surface a toast so the user knows to retry, but we don't
  // Roll back — the in-session state stays the source of truth.
  const persistRemote = useCallback(next => {
    AlertsService.savePreferences(collectId, next).catch(() => {
      // Skip the toast if the user switched collects while the save was in flight.
      if (collectId !== previousCollectId.current)
        return
      showToast(SAVE_ERROR_MSG, 'error', TOAST_CONTAINER_ID)
    })
  }, [collectId])

  const persist = useCallback(next => {
    setPreferences(next)
    persistRemote(next)
    syncUrl(next.questionIds, next.variableIds)
  }, [persistRemote])

  const reset = useCallback(() => {
    setPreferences(defaults)
    persistRemote(defaults)
    syncUrl(defaults.questionIds, defaults.variableIds)
  }, [defaults, persistRemote])

  return useMemo(() => ({
    preferences,
    setPreferences: persist,
    reset
  }), [preferences, persist, reset])
}

export default useColumnPreferences
